/**
 * Le tri et les filtres de la table des leads — module PUR, zéro React.
 *
 * Kayla, 18-08-2026 : « je puisse filtrer que ceux qui sont disponibles dans
 * ce shift-là […] par l'âge, plus de 25 ans, moins de 25 ans […] celui qui a
 * la meilleure connexion et la moins bien ».
 *
 * ⛔ SORTI DU COMPOSANT, et ce n'est pas de la décoration. Un tri qui vit dans
 * le JSX ne s'éprouve qu'en cliquant sur un en-tête, et ce qu'on éprouve alors
 * c'est le rendu, pas l'ordre. Ici c'est de l'arithmétique et de la
 * comparaison : ça se teste pour ce que c'est.
 *
 * 🚫 Un champ ABSENT n'est jamais traité comme un zéro. Un candidat sans âge
 * n'a pas « 0 an » et ne doit tomber ni dans « moins de 25 » ni dans « plus de
 * 25 » ; un candidat sans mesure de connexion n'est pas le plus lent. Les deux
 * se rangent en fin de tri et sortent des filtres qui portent sur la valeur —
 * c'est la doctrine « l'absence d'information n'est pas une valeur », et c'est
 * exactement le genre de raccourci qui fait recruter le mauvais.
 */
import { CONN_VERDICT_LABELS, DAY_LABELS_FULL, SHIFTS } from "./constants";
import { statusLabel } from "./format";
import type { Availability, CandidateListItem, ConnVerdict, DayKey, SlotKey } from "./types";
import { DAY_KEYS } from "./types";

// ─── Ce sur quoi on trie ───────────────────────────────────────────────────

export type ColonneTri =
  | "nom"
  | "prenom"
  | "sexe"
  | "age"
  | "dispos"
  | "discord"
  | "email"
  | "connexion"
  | "statut"
  | "recu_le";

export type SensTri = "asc" | "desc";

export interface Tri {
  colonne: ColonneTri;
  sens: SensTri;
}

// ─── Ce sur quoi on filtre ─────────────────────────────────────────────────

/** `null` = pas de filtre d'âge. Le seuil est porté par le filtre plutôt que
 *  figé à 25 : Kayla a cité 25 ans, elle changera d'avis sur le nombre bien
 *  avant de changer d'avis sur le geste. */
export interface FiltreAge {
  seuil: number;
  sens: "moins" | "plus";
}

export interface FiltresLeads {
  /** Shifts retenus (a/b/c/d). Vide = tous. Un candidat passe s'il couvre
   *  AU MOINS UN des shifts cochés — on cherche qui peut tenir un créneau,
   *  pas qui les tient tous. */
  shifts: SlotKey[];
  /** Jour précis, quand Kayla veut « le shift B, mais le samedi ». `null` =
   *  n'importe quel jour. */
  jour: DayKey | null;
  age: FiltreAge | null;
  /** Verdicts de connexion retenus. Vide = tous. */
  connexions: ConnVerdict[];
  /** Statuts retenus. Vide = tous — et c'est VOULU qu'il n'y ait pas de
   *  filtre par défaut : un écran qui masque en ayant l'air complet est pire
   *  qu'un écran qui montre trop. */
  statuts: string[];
  /** Recherche libre sur nom/prénom/e-mail/Discord. */
  recherche: string;
  /**
   * Montrer les liens que personne n'a remplis.
   *
   * Kayla, 18-08 : « les candidatures qui ne vont pas jusqu'au bout, faut pas
   * les mettre dedans, il y a plein de candidatures vides où il n'y a rien ».
   * Ce sont les liens créés depuis le cockpit et jamais ouverts — 18 sur 26
   * en base ce jour-là. Ils noient les vraies candidatures.
   *
   * ⚠️ Ils sont MASQUÉS par défaut, pas supprimés, et la table affiche en
   * clair combien elle en cache avec un bouton pour les rappeler. Un filtre
   * par défaut invisible est pire qu'une colonne en trop : l'écran a l'air
   * complet en ne l'étant pas.
   */
  montrerLiensVides: boolean;
  /**
   * Montrer les candidats ÉCARTÉS (`archived_at` posé par le bouton de la
   * fiche). Kayla, 12-09-2026 : « que le bouton Écarté fonctionne vraiment,
   * que ça les archive ». Même règle que les liens vides : masqués par
   * défaut, comptés à voix haute, rappelables d'un clic — jamais supprimés.
   */
  montrerEcartes: boolean;
}

/** Écarté par Kayla — il sort de la table, il ne sort pas de la base. */
export function estEcarte(c: CandidateListItem): boolean {
  return !!c.archived_at;
}

/**
 * Une candidature « vide » = personne n'a rempli le formulaire.
 *
 * Le signal est le PRÉNOM, pas le statut : `status` passe à `started` dès que
 * le lien est ouvert, avant toute saisie. Sans nom, il n'y a littéralement
 * rien à lire sur la ligne.
 */
export function estVide(c: CandidateListItem): boolean {
  return !(c.first_name ?? "").trim() && !(c.last_name ?? "").trim();
}

export const FILTRES_VIDES: FiltresLeads = {
  shifts: [],
  jour: null,
  age: null,
  connexions: [],
  statuts: [],
  recherche: "",
  montrerLiensVides: false,
  montrerEcartes: false,
};

// ─── Lectures d'une ligne ──────────────────────────────────────────────────

/** Les shifts qu'un candidat couvre, tous jours confondus (dédupliqués). */
export function shiftsCouverts(av: Availability | null | undefined): SlotKey[] {
  if (!av) return [];
  const vus = new Set<SlotKey>();
  for (const jour of DAY_KEYS) {
    for (const shift of av[jour] ?? []) vus.add(shift);
  }
  return [...vus];
}

/**
 * Ce candidat peut-il tenir ce shift ? Avec un jour, sur CE jour précisément.
 *
 * 🪤 Sans `availability`, la réponse est `false` et jamais `true` : pendant la
 * fenêtre où le cockpit tourne avant Cyrano, la clé n'existe pas dans la
 * réponse. Faire passer tout le monde « puisqu'on ne sait pas » donnerait une
 * liste qui a l'air filtrée et ne l'est pas.
 */
export function couvreShift(
  av: Availability | null | undefined,
  shift: SlotKey,
  jour: DayKey | null,
): boolean {
  if (!av) return false;
  if (jour) return (av[jour] ?? []).includes(shift);
  return DAY_KEYS.some((d) => (av[d] ?? []).includes(shift));
}

/** Le nombre de cases jour × shift cochées — sert de valeur de tri à la
 *  colonne « Dispos », qui n'a pas d'ordre naturel autrement. */
export function nombreDeCreneaux(av: Availability | null | undefined): number {
  if (!av) return 0;
  return DAY_KEYS.reduce((n, jour) => n + (av[jour]?.length ?? 0), 0);
}

/** Le nom affiché, pour le tri comme pour la recherche. */
function nomComplet(c: CandidateListItem): string {
  return `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
}

// ─── Le filtre ─────────────────────────────────────────────────────────────

export function filtrerLeads(
  leads: CandidateListItem[],
  filtres: FiltresLeads,
): CandidateListItem[] {
  const recherche = filtres.recherche.trim().toLowerCase();

  return leads.filter((c) => {
    if (!filtres.montrerLiensVides && estVide(c)) return false;
    if (!filtres.montrerEcartes && estEcarte(c)) return false;

    if (filtres.shifts.length > 0) {
      const passe = filtres.shifts.some((s) => couvreShift(c.availability, s, filtres.jour));
      if (!passe) return false;
    } else if (filtres.jour) {
      // Un jour sans shift coché : « disponible ce jour-là, peu importe quand ».
      if ((c.availability?.[filtres.jour] ?? []).length === 0) return false;
    }

    if (filtres.age) {
      // Âge inconnu → il sort du filtre. On ne le range pas d'office du
      // mauvais côté d'un seuil qu'on ne peut pas évaluer.
      if (c.age === null || c.age === undefined) return false;
      if (filtres.age.sens === "moins" && c.age >= filtres.age.seuil) return false;
      if (filtres.age.sens === "plus" && c.age < filtres.age.seuil) return false;
    }

    if (filtres.connexions.length > 0) {
      const verdict = c.connection?.verdict ?? null;
      if (verdict === null || !filtres.connexions.includes(verdict)) return false;
    }

    if (filtres.statuts.length > 0 && !filtres.statuts.includes(c.status)) return false;

    if (recherche) {
      const meule = [
        nomComplet(c),
        c.email ?? "",
        c.contact_discord ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (!meule.includes(recherche)) return false;
    }

    return true;
  });
}

// ─── Le tri ────────────────────────────────────────────────────────────────

/** Ordre des verdicts, du meilleur au moins bon. */
const RANG_CONNEXION: Record<ConnVerdict, number> = { good: 0, ok: 1, bad: 2 };

/**
 * La valeur comparable d'une ligne pour une colonne. `null` veut dire « pas
 * renseigné » et se range TOUJOURS en dernier, quel que soit le sens du tri —
 * c'est ce qui empêche « trier par meilleure connexion » de remonter en tête
 * une colonne de candidats qu'on n'a jamais mesurés.
 */
function valeurDeTri(c: CandidateListItem, colonne: ColonneTri): string | number | null {
  switch (colonne) {
    case "nom":
      return c.last_name?.toLowerCase() || null;
    case "prenom":
      return c.first_name?.toLowerCase() || null;
    case "sexe":
      return c.gender ?? null;
    case "age":
      return c.age ?? null;
    case "dispos": {
      const n = nombreDeCreneaux(c.availability);
      return n === 0 ? null : n;
    }
    case "discord":
      return c.contact_discord?.toLowerCase() || null;
    case "email":
      return c.email?.toLowerCase() || null;
    case "connexion": {
      // Le verdict d'abord (trois paliers), la latence pour départager dans
      // un même palier — deux candidats « bonne connexion » ne se valent pas.
      const verdict = c.connection?.verdict ?? null;
      if (verdict === null) return null;
      const rtt = c.connection?.rtt_median_ms ?? 0;
      return RANG_CONNEXION[verdict] * 100_000 + rtt;
    }
    case "statut":
      return c.status;
    case "recu_le":
      return c.created_at;
  }
}

export function trierLeads(leads: CandidateListItem[], tri: Tri): CandidateListItem[] {
  const facteur = tri.sens === "asc" ? 1 : -1;
  return [...leads].sort((a, b) => {
    const va = valeurDeTri(a, tri.colonne);
    const vb = valeurDeTri(b, tri.colonne);

    // Les non-renseignés ferment la marche dans les deux sens.
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;

    if (typeof va === "number" && typeof vb === "number") return (va - vb) * facteur;
    return String(va).localeCompare(String(vb), "fr") * facteur;
  });
}

/** Combien de liens vides la table cache en ce moment. Zéro quand ils sont
 *  montrés — c'est ce nombre que l'écran doit dire à voix haute. */
export function nombreDeLiensVides(
  leads: CandidateListItem[],
  filtres: FiltresLeads,
): number {
  if (filtres.montrerLiensVides) return 0;
  return leads.filter(estVide).length;
}

/** Combien d'écartés la table cache en ce moment — même contrat que
 *  `nombreDeLiensVides`. Un lien vide ET écarté ne compte qu'une fois, du
 *  côté des vides : c'est cette bande-là qui le cache d'abord. */
export function nombreDEcartes(
  leads: CandidateListItem[],
  filtres: FiltresLeads,
): number {
  if (filtres.montrerEcartes) return 0;
  return leads.filter((c) => estEcarte(c) && (filtres.montrerLiensVides || !estVide(c)))
    .length;
}

/** Filtre puis trie — l'ordre compte : trier ce qu'on va jeter est du travail
 *  perdu, et sur une liste qui grandira ça se verra. */
export function leadsAffiches(
  leads: CandidateListItem[],
  filtres: FiltresLeads,
  tri: Tri,
): CandidateListItem[] {
  return trierLeads(filtrerLeads(leads, filtres), tri);
}

// ─── Ce qui filtre EN CE MOMENT ────────────────────────────────────────────
//
// Kayla, 19-08-2026 : « j'aime pas comment c'est fait, les filtres. Pourquoi
// tu n'as pas fait comme on a dans Conversation, juste un bouton Filtre ? »
//
// Les cinq rangées de pastilles occupaient un tiers de l'écran en permanence
// pour un réglage qu'on touche une fois par session. Elles passent derrière un
// bouton — donc il faut désormais dire, SANS ouvrir le popover, ce qui est en
// train de filtrer. C'est le rôle de ces deux fonctions : le compte pour la
// pastille du bouton, la liste pour les puces qu'on peut lever une par une.
//
// ⛔ Ni la recherche ni `montrerLiensVides` (ni `montrerEcartes`) n'en font
// partie : la première a son champ, toujours visible et rempli sous les yeux
// de qui l'a tapée ; les deux autres ont leur propre bande qui dit en clair
// combien de lignes sont masquées. Les
// compter deux fois donnerait un bouton qui annonce « 2 filtres » quand on n'a
// rien réglé du tout.

/** Un filtre en cours, nommé — et qu'on peut lever seul. */
export interface FiltreActif {
  /** Identité stable (la catégorie) — clé React et cible des tests. */
  cle: "shifts" | "jour" | "age" | "connexions" | "statuts";
  /** Ce que Kayla lit sur la puce. */
  libelle: string;
  /** Rend l'état où CE filtre est levé, les autres intacts. */
  lever: (f: FiltresLeads) => FiltresLeads;
}

/**
 * Les filtres en cours, dans l'ordre où ils apparaissent dans le popover.
 *
 * 🪤 Une CATÉGORIE = une puce, même si elle retient cinq valeurs. Compter les
 * valeurs ferait exploser la ligne dès qu'on coche trois shifts, et surtout on
 * ne pourrait plus lever « les shifts » d'un geste — ce qui est précisément
 * ce qu'on veut faire quand on a fini de chercher qui tient le samedi soir.
 */
export function filtresActifs(f: FiltresLeads): FiltreActif[] {
  const actifs: FiltreActif[] = [];

  if (f.shifts.length > 0) {
    const lettres = SHIFTS.filter((s) => f.shifts.includes(s.key)).map((s) => s.letter);
    actifs.push({
      cle: "shifts",
      libelle: `Shift ${lettres.join(", ")}`,
      lever: (x) => ({ ...x, shifts: [] }),
    });
  }

  if (f.jour !== null) {
    actifs.push({
      cle: "jour",
      libelle: DAY_LABELS_FULL[f.jour],
      lever: (x) => ({ ...x, jour: null }),
    });
  }

  if (f.age !== null) {
    const { seuil, sens } = f.age;
    actifs.push({
      cle: "age",
      libelle: sens === "moins" ? `Moins de ${seuil} ans` : `${seuil} ans et plus`,
      lever: (x) => ({ ...x, age: null }),
    });
  }

  if (f.connexions.length > 0) {
    const noms = f.connexions.map((v) => CONN_VERDICT_LABELS[v]);
    actifs.push({
      cle: "connexions",
      libelle: `Connexion : ${noms.join(", ")}`,
      lever: (x) => ({ ...x, connexions: [] }),
    });
  }

  if (f.statuts.length > 0) {
    const noms = f.statuts.map((st) => statusLabel(st).toLowerCase());
    actifs.push({
      cle: "statuts",
      libelle: `Test : ${noms.join(", ")}`,
      lever: (x) => ({ ...x, statuts: [] }),
    });
  }

  return actifs;
}

/** Le nombre affiché sur la pastille du bouton « Filtres ». */
export function nombreDeFiltresActifs(f: FiltresLeads): number {
  return filtresActifs(f).length;
}
